import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@rickylandino/react-messages';
import { Table, Checkbox, Select } from 'antd';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import NumberFormat from 'react-number-format';
import Moment from 'moment';
import { firstBy } from "thenby";
import { groupBy } from '../../Helpers/GroupBy';
import Globals from '../../../config/globals';
import { useTechSummaryData } from '../../../hooks/useWidgetData';
import { useSaveWidgetFilters } from '../../../hooks/useDashboard';
import { getExamTechsByFacility } from '../../../common/services/ReportService';
import { userAssociatedFacilities, allCategories } from '../../../common/services/ApiService';

const { Option, OptGroup } = Select;

/**
 * TechSummaryWidget component
 * Shows technologist exam summary with optional detailed exam breakdown
 * Form is identical to Report/Static/TechDetailsReportModal for backwards compatibility
 * @param {Object} props
 * @param {boolean} [props.isCollapsed=false] - Whether the widget is collapsed
 * @param {boolean} [props.isExpanded=false] - Whether the widget is in fullscreen mode
 * @param {Function} [props.setIsLoading] - Callback to set loading state for parent
 * @param {Function} [props.setSharedState] - Callback to update shared state (including refetch)
 * @param {Object} [props.widget] - Widget configuration object
 */
export default function TechSummaryWidget({ 
    isCollapsed = false, 
    isExpanded = false, 
    setIsLoading, 
    setSharedState,
    widget 
}) {
    const { Option } = Select;
    const queryClient = useQueryClient();
    const saveFiltersMutation = useSaveWidgetFilters();

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);

    // Expanded rows state
    const [expandedRowKeys, setExpandedRowKeys] = useState(new Set());

    // Form state - matches TechDetailsReportModal exactly
    const [allFacilities, setAllFacilities] = useState([]);
    const [selectedFacility, setSelectedFacility] = useState('');
    const [availableTechs, setAvailableTechs] = useState([]);
    const [selectedTech, setSelectedTech] = useState([]);
    const [availableExamTypes, setAvailableExamTypes] = useState([]);
    const [selectedExamTypes, setSelectedExamTypes] = useState([]);
    const [sortedExamTypes, setSortedExamTypes] = useState([]);
    const [selectedDates, setSelectedDates] = useState({
        startDate: null,
        endDate: null
    });
    const [showDetails, setShowDetails] = useState(false);
    const [allCategoriesState, setAllCategoriesState] = useState([]);
    const [examTypeOptions, setExamTypeOptions] = useState([]);
    const [loaded, setLoaded] = useState(false);

    // Query parameters state (triggers data fetch)
    const [queryParams, setQueryParams] = useState(null);

    // Refs for auto-expand logic
    const previousShowDetails = useRef(false);
    const previousResultsLength = useRef(0);
    const isInitialLoad = useRef(true);

    // Parse saved filter config
    const savedFilters = useMemo(() => {
        if (!widget?.filterConfig) return null;
        
        // filterConfig might be a string (from save) or object (from dashboard API)
        if (typeof widget.filterConfig === 'string') {
            try {
                return JSON.parse(widget.filterConfig);
            } catch {
                return null;
            }
        }
        
        // Already an object
        console.log('TechSummaryWidget - Loaded savedFilters:', widget.filterConfig);
        return widget.filterConfig;
    }, [widget?.filterConfig]);

    // Determine when to load data
    const shouldLoadData = !isCollapsed || isExpanded;

    // Fetch data using React Query
    const {
        data: results = [],
        isLoading,
        isFetching,
        isError,
        error,
        refetch
    } = useTechSummaryData(queryParams, shouldLoadData && queryParams !== null);

    // Notify parent of loading state for fullscreen overlay
    useEffect(() => {
        if (setIsLoading && isExpanded) {
            setIsLoading(isLoading || isFetching);
        }
    }, [isLoading, isFetching, isExpanded, setIsLoading]);

    // Show error toast when data fails to load
    useEffect(() => {
        if (shouldLoadData && isError) {
            console.error('Failed to load tech summary:', error);
            toast.error(`Failed to load tech summary: ${error?.message || 'Unknown error'}`);
        }
    }, [shouldLoadData, isError, error]);

    // Set refetch function in shared state
    useEffect(() => {
        if (setSharedState && refetch) {
            setSharedState(prev => ({ ...prev, refetch }));
        }
    }, [setSharedState, refetch]);

    /**
     * Handle facility selection change - matches TechDetailsReportModal exactly
     */
    const handleFacilityChange = useCallback((facilityId, clearSelections = true) => {
        console.log('TechSummaryWidget - handleFacilityChange:', facilityId, 'clearSelections:', clearSelections);
        getExamTechsByFacility(facilityId).then(data => {
            let techs = [...data];
            if (techs.length === 0) {
                techs = allFacilities.find(f => f.facilityModel.facilityID === facilityId)?.availableTechs || [];
            }

            let availableExamTypes = allFacilities.find(f => f.facilityModel.facilityID === facilityId)?.availableExamTypes || [];

            let fet = [...availableExamTypes];
            fet.sort(firstBy("categoryID"));

            var o = groupBy(fet, 'categoryID');

            var sortedExamTypes = [];

            for (const property in o) {
                o[property].sort(firstBy("examType"));
                sortedExamTypes.push(...o[property]);
            }

            let examTypeOptions = [];

            sortedExamTypes.forEach((type, idx) => {
                if (type.categoryID !== sortedExamTypes[idx - 1]?.categoryID) {
                    let newLabelObj = { 
                        label: allCategoriesState?.find(cat => cat.categoryID === type.categoryID)?.description, 
                        options: [] 
                    };
                    examTypeOptions.push(newLabelObj);
                }

                let newOption = { label: type.examType, value: type.examTypeID };
                examTypeOptions[examTypeOptions.length - 1].options.push(newOption);
            });

            setSelectedFacility(facilityId);
            setAvailableTechs(techs);
            
            // Only clear selections if not restoring from saved filters
            if (clearSelections) {
                setSelectedTech([]);
                setSelectedExamTypes([]);
            }
            
            setAvailableExamTypes(availableExamTypes);
            setSortedExamTypes(sortedExamTypes);
            setExamTypeOptions(examTypeOptions);
            
            console.log('TechSummaryWidget - Built examTypeOptions:', examTypeOptions.length, 'groups');
        });
    }, [allFacilities, allCategoriesState]);

    // Load facilities and categories on mount (using RxJS observables like the modal)
    useEffect(() => {
        let facilitiesLoaded = false;
        let categoriesLoaded = false;
        let loadedFacilities = null;
        
        const checkAndInitialize = () => {
            if (facilitiesLoaded && categoriesLoaded && loadedFacilities) {
                console.log('TechSummaryWidget - Both facilities and categories loaded, initializing...');
                
                // Support both facilityId (current) and selectedFacilities (legacy) formats
                const savedFacilityId = savedFilters?.facilityId || savedFilters?.selectedFacilities?.[0];
                
                // Auto-select if only one facility
                if (loadedFacilities.length === 1 && !savedFacilityId) {
                    const facilityId = loadedFacilities[0].facilityModel.facilityID;
                    setSelectedFacility(facilityId);
                    handleFacilityChange(facilityId, true); // Clear selections
                } else if (savedFacilityId) {
                    setSelectedFacility(savedFacilityId);
                    handleFacilityChange(savedFacilityId, false); // Don't clear - we're restoring
                }
                
                setLoaded(true);
            }
        };
        
        const subscription1 = userAssociatedFacilities.subscribe(facilities => {
            if (facilities?.length > 0) {
                console.log('TechSummaryWidget - Facilities loaded:', facilities.length);
                setAllFacilities(facilities);
                loadedFacilities = facilities;
                facilitiesLoaded = true;
                checkAndInitialize();
            }
        });

        const subscription2 = allCategories.subscribe(categories => {
            console.log('TechSummaryWidget - Categories loaded:', categories?.length || 0);
            setAllCategoriesState(categories || []);
            categoriesLoaded = true;
            checkAndInitialize();
        });

        return () => {
            subscription1.unsubscribe();
            subscription2.unsubscribe();
        };
    }, [savedFilters?.facilityId, savedFilters?.selectedFacilities, handleFacilityChange]);

    // Restore saved filters after facilities load
    useEffect(() => {
        if (!loaded || !savedFilters || !isInitialLoad.current) return;

        isInitialLoad.current = false;

        // Restore dates immediately
        if (savedFilters.startDate || savedFilters.endDate) {
            setSelectedDates({
                startDate: savedFilters.startDate ? Moment(savedFilters.startDate).toDate() : null,
                endDate: savedFilters.endDate ? Moment(savedFilters.endDate).toDate() : null
            });
        }
        
        // Restore showDetails immediately
        if (savedFilters.showDetailed !== undefined) {
            setShowDetails(savedFilters.showDetailed);
        }
    }, [loaded, savedFilters]);

    // Restore techs and exam types after they're loaded from handleFacilityChange
    useEffect(() => {
        if (!savedFilters || isInitialLoad.current) return;
        
        // Only restore if we have available techs loaded (indicates facility change completed)
        if (savedFilters.technologistIds && availableTechs.length > 0) {
            console.log('TechSummaryWidget - Restoring techs:', savedFilters.technologistIds);
            setSelectedTech(savedFilters.technologistIds);
        }
        
        // Wait for examTypeOptions to be built (requires availableExamTypes AND allCategoriesState)
        if (savedFilters.examTypeIds && examTypeOptions.length > 0) {
            console.log('TechSummaryWidget - Restoring exam types:', savedFilters.examTypeIds);
            setSelectedExamTypes(savedFilters.examTypeIds);
        }
    }, [savedFilters, availableTechs, examTypeOptions]);

    // Auto-expand rows when showDetailed changes or results load
    useEffect(() => {
        if (!queryParams?.showDetails || isLoading || !results) return;

        const showDetailsChanged = previousShowDetails.current !== queryParams.showDetails;
        const resultsChanged = previousResultsLength.current !== results.length;

        if (showDetailsChanged || resultsChanged) {
            // Auto-expand all rows that have details
            const keysToExpand = new Set();
            results.forEach(tech => {
                if (tech.details && tech.details.length > 0) {
                    keysToExpand.add(tech.userId);
                }
            });
            setExpandedRowKeys(keysToExpand);

            previousShowDetails.current = queryParams.showDetails;
            previousResultsLength.current = results.length;
        }
    }, [queryParams?.showDetails, results, isLoading]);

    /**
     * Handle tech selection change - matches TechDetailsReportModal exactly
     */
    const handleTechChange = useCallback((selectedTechs) => {
        // Check if "Select All" was just selected
        if (selectedTechs.includes("Select All") && !selectedTech.includes("Select All")) {
            const allTechIds = availableTechs.map(t => t.userID);
            setSelectedTech(allTechIds);
        }
        // Check if "Select All" was deselected
        else if (!selectedTechs.includes("Select All") && selectedTech.includes("Select All")) {
            setSelectedTech([]);
        }
        // Check if "Select All" is currently selected but user selected/deselected individual items
        else if (selectedTech.includes("Select All") && selectedTechs.includes("Select All")) {
            const filteredTechs = selectedTechs.filter(t => t !== "Select All");
            setSelectedTech(filteredTechs);
        }
        // Normal selection without "Select All"
        else {
            const allIndividualTechs = availableTechs.map(t => t.userID);
            const hasAllItems = allIndividualTechs.every(tech => selectedTechs.includes(tech));
            
            if (hasAllItems && allIndividualTechs.length === selectedTechs.length) {
                setSelectedTech(["Select All", ...selectedTechs]);
            } else {
                setSelectedTech(selectedTechs);
            }
        }
    }, [selectedTech, availableTechs]);

    /**
     * Handle exam types selection change - matches TechDetailsReportModal exactly
     */
    const handleExamTypesChange = useCallback((newSelectedExamTypes) => {
        // Check if "Select All" was just selected
        if (newSelectedExamTypes.includes("Select All") && !selectedExamTypes.includes("Select All")) {
            const allExamTypeIds = availableExamTypes.map(et => et.examTypeID);
            setSelectedExamTypes(allExamTypeIds);
        }
        // Check if "Select All" was deselected
        else if (!newSelectedExamTypes.includes("Select All") && selectedExamTypes.includes("Select All")) {
            setSelectedExamTypes([]);
        }
        // Check if "Select All" is currently selected but user selected/deselected individual items
        else if (selectedExamTypes.includes("Select All") && newSelectedExamTypes.includes("Select All")) {
            const filteredExamTypes = newSelectedExamTypes.filter(et => et !== "Select All");
            setSelectedExamTypes(filteredExamTypes);
        }
        // Normal selection without "Select All"
        else {
            const allIndividualExamTypes = availableExamTypes.map(et => et.examTypeID);
            const hasAllItems = allIndividualExamTypes.every(examType => newSelectedExamTypes.includes(examType));
            
            if (hasAllItems && allIndividualExamTypes.length === newSelectedExamTypes.length) {
                setSelectedExamTypes(["Select All", ...newSelectedExamTypes]);
            } else {
                setSelectedExamTypes(newSelectedExamTypes);
            }
        }
    }, [selectedExamTypes, availableExamTypes]);

    /**
     * Handle date change
     */
    const handleNewDateChange = useCallback((name, date) => {
        setSelectedDates(prev => ({ ...prev, [name]: date }));
    }, []);

    /**
     * Handle show details checkbox change
     */
    const handleInputChange = useCallback(() => {
        setShowDetails(prev => !prev);
    }, []);

    /**
     * Handle form submission
     */
    const handleSubmit = useCallback((e) => {
        e?.preventDefault();

        const endDate = selectedDates.endDate ? Moment(selectedDates.endDate).format("YYYY-MM-DD") : null;
        const startDate = selectedDates.startDate ? Moment(selectedDates.startDate).format("YYYY-MM-DD") : null;

        // Validation
        if (!selectedFacility) {
            toast.error('Please select a facility');
            return;
        }

        // Filter out "Select All" from selected values
        const techValue = selectedTech.filter(t => t !== "Select All");
        const examTypesValue = selectedExamTypes.filter(et => et !== "Select All");

        // Build query params
        const params = {
            startDate: startDate || '',
            endDate: endDate || '',
            selectedFacilities: [selectedFacility],
            selectedTechs: techValue,
            selectedExamTypes: examTypesValue,
            showDetails: showDetails,
            ianaTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };

        // Save filters
        if (widget?.userDashboardWidgetId) {
            const filterConfig = {
                facilityId: selectedFacility,
                technologistIds: techValue,
                examTypeIds: examTypesValue,
                startDate: startDate,
                endDate: endDate,
                showDetailed: showDetails
            };
            
            saveFiltersMutation.mutate({
                userDashboardWidgetId: widget.userDashboardWidgetId,
                filterConfig
            });
        }

        // Set query params (triggers React Query fetch)
        setQueryParams(params);

        // Reset pagination
        setCurrentPage(1);

        // Clear expansion state
        setExpandedRowKeys(new Set());

        // Reset auto-expand tracking
        previousShowDetails.current = false;
        previousResultsLength.current = 0;
    }, [
        selectedDates,
        selectedFacility,
        selectedTech,
        selectedExamTypes,
        showDetails,
        widget?.userDashboardWidgetId,
        saveFiltersMutation
    ]);

    /**
     * Handle row click to toggle expansion
     */
    const handleRowClick = useCallback((record) => {
        if (!queryParams?.showDetails || !record.details || record.details.length === 0) {
            return;
        }

        setExpandedRowKeys(prev => {
            const newSet = new Set(prev);
            if (newSet.has(record.userId)) {
                newSet.delete(record.userId);
            } else {
                newSet.add(record.userId);
            }
            return newSet;
        });
    }, [queryParams?.showDetails]);

    /**
     * Calculate total exams across all technologists
     */
    const totalExams = useMemo(() => {
        return results.reduce((sum, tech) => sum + (tech.totalExams || 0), 0);
    }, [results]);

    /**
     * Client-side pagination
     */
    const paginatedResults = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return results.slice(startIndex, endIndex);
    }, [results, currentPage, pageSize]);

    /**
     * Table columns
     */
    const columns = useMemo(() => [
        {
            title: 'Technologist',
            dataIndex: 'techName',
            key: 'techName',
            className: 'font-medium'
        },
        {
            title: 'Total Exams',
            dataIndex: 'totalExams',
            key: 'totalExams',
            align: 'right',
            className: 'font-semibold'
        }
    ], []);

    /**
     * Render expanded row content
     */
    const renderExpandedRow = useCallback((record) => {
        const isDark = Globals.theme === 'dark';
        if (!record.details || record.details.length === 0) {
            return (
                <div className="px-4 py-2 text-sm" style={{ color: isDark ? '#71717a' : '#9ca3af' }}>
                    No exam details available
                </div>
            );
        }

        return (
            <div className="px-4 py-2" style={{ backgroundColor: isDark ? '#18181b' : '#f9fafb' }}>
                <div className="text-xs font-medium mb-2" style={{ color: isDark ? '#71717a' : '#9ca3af' }}>
                    Exam Details ({record.details.length} exam{record.details.length !== 1 ? 's' : ''})
                </div>
                <div className="space-y-1">
                    {record.details.map((detail, idx) => (
                        <div
                            key={idx}
                            className="text-xs rounded p-1.5"
                            style={{
                                backgroundColor: isDark ? '#27272a' : '#ffffff',
                                border: `1px solid ${isDark ? '#3f3f46' : '#e5e7eb'}`
                            }}
                        >
                            <div className="flex items-center justify-between gap-2 mb-0.5">
                                <span className="truncate min-w-0" style={{ color: isDark ? '#e4e4e7' : '#18181b' }}>
                                    {detail.patientName}
                                </span>
                                <span className="flex-shrink-0 whitespace-nowrap" style={{ minWidth: '80px', color: isDark ? '#a1a1aa' : '#6b7280' }}>
                                    {detail.examDateTime ? Moment(detail.examDateTime).format('MM/DD/YYYY') : 'N/A'}
                                </span>
                            </div>
                            <div className="flex items-center justify-between gap-2">
                                <span className="truncate min-w-0" style={{ color: isDark ? '#d4d4d8' : '#52525b' }}>
                                    {detail.examType}
                                </span>
                                <span className="font-mono flex-shrink-0 whitespace-nowrap" style={{ minWidth: '120px', fontSize: '0.7rem', color: isDark ? '#a1a1aa' : '#6b7280' }}>
                                    {detail.mrn}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }, []);

    /**
     * Get row class name based on expansion and detail availability
     */
    const getRowClassName = useCallback((record) => {
        const hasDetails = queryParams?.showDetails && record.details && record.details.length > 0;
        return hasDetails ? 'cursor-pointer' : '';
    }, [queryParams?.showDetails]);

    // COLLAPSED STATE - No data shown when collapsed
    if (isCollapsed && !isExpanded) {
        return (
            <div className="flex flex-col items-center justify-center py-4">
                <div 
                    className="text-sm"
                    style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}
                >
                    Expand to view tech summary
                </div>
            </div>
        );
    }

    // EXPANDED STATE - Show form and results
    return (
        <div className="space-y-4">
            {/* Filter Form - Matches TechDetailsReportModal exactly */}
            {loaded && (
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Date Range */}
                        <div>
                            <div className="form-label">
                                Start Range <span className="color-pink">*</span>
                            </div>
                            <DatePicker
                                onChange={(date) => handleNewDateChange('startDate', date)}
                                selectsStart
                                startDate={selectedDates.startDate}
                                endDate={selectedDates.endDate}
                                className="form-control-custom w-full"
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                                placeholderText='Start Date'
                                selected={selectedDates.startDate && Moment(selectedDates.startDate).toDate()}
                                customInput={<NumberFormat format="##/##/####" mask="_" />}
                            />
                        </div>
                        
                        <div>
                            <div className="form-label">
                                End Range <span className="color-pink">*</span>
                            </div>
                            <DatePicker
                                onChange={(date) => handleNewDateChange('endDate', date)}
                                selectsEnd
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                                startDate={selectedDates.startDate}
                                endDate={selectedDates.endDate}
                                className="form-control-custom w-full"
                                placeholderText='End Date'
                                selected={selectedDates.endDate && Moment(selectedDates.endDate).toDate()}
                                customInput={<NumberFormat format="##/##/####" mask="_" />}
                            />
                        </div>

                        {/* Facility */}
                        <div>
                            <div className="form-label">
                                Facility <span className="color-pink">*</span>
                            </div>
                            {allFacilities.length === 1 ? (
                                <div>{allFacilities[0].facilityModel.facilityName}</div>
                            ) : (
                                <Select
                                    allowClear
                                    placeholder="Please select"
                                    onChange={handleFacilityChange}
                                    className="form-control-custom w-100"
                                    bordered={false}
                                    value={selectedFacility}
                                    showSearch
                                    virtual={false}
                                    filterOption={(input, option) => 
                                        (option.children[0] + option.children[1]).toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    {allFacilities.map((fac, idx) => (
                                        <Option key={fac.facilityModel.facilityID} value={fac.facilityModel.facilityID}>
                                            {fac.facilityModel.facilityNumber && fac.facilityModel.facilityNumber + " - "}
                                            {fac.facilityModel.facilityName}
                                        </Option>
                                    ))}
                                </Select>
                            )}
                        </div>

                        {/* Technologists */}
                        <div>
                            <div className="form-label">Technologist</div>
                            <Select
                                mode="multiple"
                                maxTagCount={1}
                                allowClear
                                placeholder="Please select"
                                onChange={handleTechChange}
                                className="form-control-custom w-100"
                                bordered={false}
                                virtual={false}
                                value={selectedTech}
                            >
                                <Option key="Select All" value="Select All">Select All</Option>
                                {availableTechs.map((t, idx) => (
                                    <Option key={t.userID} value={t.userID}>
                                        {t.fName} {t.lName}
                                    </Option>
                                ))}
                            </Select>
                        </div>

                        {/* Exam Types */}
                        <div className="md:col-span-2">
                            <div className="form-label">Exam Types</div>
                            <Select
                                mode="multiple"
                                maxTagCount={3}
                                allowClear
                                placeholder="Please select"
                                onChange={handleExamTypesChange}
                                className="form-control-custom w-100"
                                bordered={false}
                                virtual={false}
                                value={selectedExamTypes}
                            >
                                <Option key="Select All" value="Select All">Select All</Option>
                                {examTypeOptions.map((group, groupIdx) => (
                                    <OptGroup key={groupIdx} label={group.label}>
                                        {group.options.map((option) => (
                                            <Option key={option.value} value={option.value}>
                                                {option.label}
                                            </Option>
                                        ))}
                                    </OptGroup>
                                ))}
                            </Select>
                        </div>

                        {/* Show Detailed Checkbox */}
                        <div className="md:col-span-2">
                            <Checkbox
                                checked={showDetails}
                                onChange={handleInputChange}
                            >
                                <span style={{ color: Globals.theme === 'dark' ? '#d4d4d8' : '#52525b' }}>
                                    Show detailed exam breakdown
                                </span>
                            </Checkbox>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={queryParams && isFetching}
                            className="px-4 py-2 rounded-lg font-medium transition-colors"
                            style={{
                                backgroundColor: Globals.theme === 'dark' ? '#ec4899' : '#db2777',
                                color: 'white',
                                opacity: (queryParams && isFetching) ? 0.6 : 1
                            }}
                            onMouseEnter={(e) => {
                                if (!(queryParams && isFetching)) {
                                    e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#db2777' : '#be185d';
                                }
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#ec4899' : '#db2777';
                            }}
                        >
                            {(queryParams && isFetching) ? 'Loading...' : 'Generate Report'}
                        </button>
                    </div>
                </form>
            )}

            {/* Results Table */}
            {queryParams && (
                <>
                    {results.length === 0 && !isLoading ? (
                        <div className="text-center py-8 text-sm" style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}>
                            No results found for the selected criteria
                        </div>
                    ) : (
                        <>
                            {/* Total Summary */}
                            <div className="text-sm" style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}>
                                Results (<span style={{ color: Globals.theme === 'dark' ? '#e4e4e7' : '#18181b' }}>{results.length} Technologist{results.length !== 1 ? 's' : ''}</span>, <span style={{ color: Globals.theme === 'dark' ? '#e4e4e7' : '#18181b' }}>{totalExams} Total Exam{totalExams !== 1 ? 's' : ''}</span>)
                            </div>

                            {/* Data Table */}
                            <Table
                                columns={columns}
                                dataSource={paginatedResults}
                                rowKey="userId"
                                pagination={{
                                    current: currentPage,
                                    pageSize: pageSize,
                                    total: results.length,
                                    onChange: (page, newPageSize) => {
                                        setCurrentPage(page);
                                        if (newPageSize !== pageSize) {
                                            setPageSize(newPageSize);
                                            setCurrentPage(1);
                                        }
                                    },
                                    pageSizeOptions: ['25', '50', '100', '200'],
                                    showSizeChanger: true,
                                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} technologists`
                                }}
                                size="small"
                                loading={isLoading}
                                expandable={queryParams.showDetails ? {
                                    expandedRowKeys: Array.from(expandedRowKeys),
                                    onExpand: (expanded, record) => handleRowClick(record),
                                    expandedRowRender: renderExpandedRow,
                                    expandIcon: () => null
                                } : undefined}
                                onRow={(record) => ({
                                    onClick: () => handleRowClick(record),
                                    className: getRowClassName(record)
                                })}
                                className={`[&_table]:table-fixed${Globals.theme === 'dark' ? ' [&_.ant-table-expanded-row>td]:!bg-[#18181b]' : ''}`}
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
}
